import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoIcon from '@mui/icons-material/Info';

const InfoDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          backgroundImage: 'none',
          border: `1px solid ${colors.primary[300]}`
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: colors.primary[500], 
        color: colors.grey[100],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.primary[300]}`
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoIcon sx={{ color: colors.blueAccent[400] }} />
          <Typography variant="h5" fontWeight="600">
            Market Information
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.grey[300] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: colors.primary[400] }}>
        <Box mb={3}>
          <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="600" mb={2}>
            Welcome to the Enhanced Splinterlands Market
          </Typography>
          <Typography variant="body1" color={colors.grey[200]} mb={2}>
            This marketplace charges a 4% market fee and distributes it transparently across the community.
            Additionally, we work closely with SplTrader to provide an advanced bid system where 90% of our bids win against other bid services.
            See the detailed breakdown below of how your fees contribute to the ecosystem.
          </Typography>
        </Box>

        <Divider sx={{ backgroundColor: colors.primary[300], mb: 3 }} />

        <Box mb={3}>
          <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
            <InfoIcon />
            SplTrader Partnership & Bid System
          </Typography>
          <Box 
            p={2} 
            backgroundColor={colors.primary[500]}
            borderRadius={1}
            border={`1px solid ${colors.greenAccent[400]}50`}
            mb={2}
          >
            <Typography variant="body2" color={colors.grey[200]} fontWeight="600" mb={1}>
              Advanced Bid System Features:
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>
                  <CheckCircleIcon sx={{ color: colors.greenAccent[400], fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="90% win rate against other bid services" 
                  primaryTypographyProps={{ fontSize: '0.875rem', color: colors.grey[200] }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>
                  <TrendingUpIcon sx={{ color: colors.yellow[400], fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Automatic BCX reduction when cards are purchased" 
                  primaryTypographyProps={{ fontSize: '0.875rem', color: colors.grey[200] }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>
                  <LocalFireDepartmentIcon sx={{ color: colors.redAccent[400], fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="0.3% of bid transactions sent to SPS burn cycle" 
                  primaryTypographyProps={{ fontSize: '0.875rem', color: colors.grey[200] }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>
                  <EmojiEventsIcon sx={{ color: colors.blueAccent[400], fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Bids must be active to participate in marketplace" 
                  primaryTypographyProps={{ fontSize: '0.875rem', color: colors.grey[200] }}
                />
              </ListItem>
            </List>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
            <InfoIcon />
            4% Market Fee Distribution
          </Typography>
          <Box 
            p={2} 
            backgroundColor={colors.primary[500]}
            borderRadius={1}
            border={`1px solid ${colors.primary[300]}`}
            mb={2}
          >
            <Typography variant="body2" color={colors.grey[200]} fontWeight="600" mb={1}>
              From every 4% market fee collected:
            </Typography>
          </Box>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <Chip label="2.6%" size="small" sx={{ 
                  backgroundColor: colors.greenAccent[400] + '20',
                  color: colors.greenAccent[400],
                  fontWeight: 'bold',
                  minWidth: '45px'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="Cashback to Traders (65%)" 
                secondary="Direct cashback to your wallet • Wallet: elmo.market"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="0.3%" size="small" sx={{ 
                  backgroundColor: colors.redAccent[400] + '20',
                  color: colors.redAccent[400],
                  fontWeight: 'bold',
                  minWidth: '45px'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="SPS Burn Cycle (7.5%)" 
                secondary="Purchase SPS tokens and send to burn cycle • Operations: elmo-market.sps"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="0.1%" size="small" sx={{ 
                  backgroundColor: colors.yellow[400] + '20',
                  color: colors.yellow[400],
                  fontWeight: 'bold',
                  minWidth: '45px'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="Monthly Reward Pool (2.5%)" 
                secondary="Additional monthly leaderboard rewards • Pool: elmo.pool"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="0.06%" size="small" sx={{ 
                  backgroundColor: colors.blueAccent[400] + '20',
                  color: colors.blueAccent[400],
                  fontWeight: 'bold',
                  minWidth: '45px'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="Aqualis Support (1.5%)" 
                secondary="Supporting the Aqualis Ecosystem"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="0.06%" size="small" sx={{ 
                  backgroundColor: colors.greenAccent[500] + '20',
                  color: colors.greenAccent[500],
                  fontWeight: 'bold',
                  minWidth: '45px'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="PNS Token Support (1.5%)" 
                secondary="Strengthening PNS token community"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="0.68%" size="small" sx={{ 
                  backgroundColor: colors.grey[400] + '20',
                  color: colors.grey[400],
                  fontWeight: 'bold',
                  minWidth: '45px'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="Operations & Server Costs (17%)" 
                secondary="Elmo market operations, server costs, and development"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
          </List>
        </Box>

        <Box mb={3}>
          <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
            <AccountBalanceWalletIcon />
            Your Benefits
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <EmojiEventsIcon sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Direct Cashback" 
                secondary="Receive 2.6% cashback on all your marketplace transactions"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LocalFireDepartmentIcon sx={{ color: colors.redAccent[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="SPS Ecosystem Support" 
                secondary="Your trades contribute to SPS token burns, strengthening the entire ecosystem"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TrendingUpIcon sx={{ color: colors.yellow[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Monthly Competitions" 
                secondary="Compete for additional rewards from the 0.1% monthly pool"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
          </List>
        </Box>

        <Box 
          p={2} 
          backgroundColor={colors.primary[500]}
          borderRadius={1}
          border={`1px solid ${colors.primary[300]}`}
        >
          <Typography variant="body2" color={colors.grey[200]} fontStyle="italic">
            💡 <strong>Tip:</strong> Click the leaderboard button to see your current ranking and compete for monthly bonus rewards from the 0.1% reward pool!
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        backgroundColor: colors.primary[500], 
        borderTop: `1px solid ${colors.primary[300]}`,
        p: 2
      }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            backgroundColor: colors.blueAccent[600],
            color: colors.grey[100],
            '&:hover': {
              backgroundColor: colors.blueAccent[700]
            }
          }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoDialog;
